import React, {useCallback, useState} from 'react';

import {usePageProjectAndEntityTarget} from '../hooks';
import {JsonObject} from '../utilTypes';

type JobDrawerStateContextType = {
  overrides: string;
  overridesError: string;
  structuredOverrides: JsonObject;
  structuredFileOverrides: JsonObject | undefined;
  targetEntityName: string;
  targetProjectName: string;
  hasUserEdits: boolean;
  onOverridesChange: (value: string) => void;
  onStructuredOverridesChange: (config: JsonObject, files?: JsonObject) => void;
  setOverridesError: (error: string) => void;
  setTargetEntityName: (entityName: string) => void;
  setTargetProjectName: (projectName: string) => void;
  initializeForJob: (jobId: string) => void;
};

const JobDrawerStateContext = React.createContext<
  JobDrawerStateContextType | undefined
>(undefined);

// this context persists user edits (overrides and destination project) when
// navigating between views or closing/reopening the drawer. this prevents users
// from losing their work when they go to create secrets or when reopening and closing the drawer.
export const JobDrawerStateProvider: React.FC<{
  children: React.ReactNode;
  rerunFromRunName?: string;
}> = ({children, rerunFromRunName}) => {
  const target = usePageProjectAndEntityTarget();

  // use the ref to prevent infinite re-renders
  const initialTargetEntityName = React.useRef(target.targetEntityName);
  const initialTargetProjectName = React.useRef(target.targetProjectName);

  const [overrides, setOverrides] = useState('');
  const [overridesError, setOverridesError] = useState('');
  const [structuredOverrides, setStructuredOverrides] = useState<JsonObject>(
    {}
  );
  const [structuredFileOverrides, setStructuredFileOverrides] = useState<
    JsonObject | undefined
  >(undefined);

  const [targetEntityName, setTargetEntityName] = useState<string>(
    initialTargetEntityName.current
  );
  const [targetProjectName, setTargetProjectName] = useState<string>(
    initialTargetProjectName.current
  );

  const [hasUserEdits, setHasUserEdits] = useState(false);

  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [currentRunName, setCurrentRunName] = useState<string | null>(
    rerunFromRunName ?? null
  );

  const initializeForJob = useCallback(
    (jobId: string) => {
      if (currentJobId !== jobId || currentRunName !== rerunFromRunName) {
        // only reset state if we're switching from one job/run to another
        // (not when initializing from null, which preserves reopened state)
        const isSwitchingJobOrRun =
          (currentJobId !== null && currentJobId !== jobId) ||
          (currentRunName !== null && currentRunName !== rerunFromRunName);

        if (isSwitchingJobOrRun) {
          // clear state when job or run changes
          setOverrides('');
          setOverridesError('');
          setStructuredOverrides({});
          setStructuredFileOverrides(undefined);
          setHasUserEdits(false);
          // reset target project to page defaults only when switching jobs
          setTargetEntityName(initialTargetEntityName.current);
          setTargetProjectName(initialTargetProjectName.current);
        }

        setCurrentJobId(jobId);
        setCurrentRunName(rerunFromRunName ?? null);
      }
    },
    [currentJobId, currentRunName, rerunFromRunName]
  );

  const onOverridesChange = useCallback((value: string) => {
    setOverrides(value);
    setOverridesError('');
    setHasUserEdits(true);
  }, []);

  const onStructuredOverridesChange = useCallback(
    (config: JsonObject, files?: JsonObject) => {
      const overridesStr = JSON.stringify(
        {
          args: [],
          entry_point: [],
          run_config: config,
          files,
        },
        null,
        4
      );
      setOverrides(overridesStr);
      setStructuredOverrides(config);
      setStructuredFileOverrides(files);
      setOverridesError('');
      setHasUserEdits(true);
    },
    []
  );

  const value: JobDrawerStateContextType = {
    overrides,
    overridesError,
    structuredOverrides,
    structuredFileOverrides,
    targetEntityName,
    targetProjectName,
    hasUserEdits,
    onOverridesChange,
    onStructuredOverridesChange,
    setOverridesError,
    setTargetEntityName,
    setTargetProjectName,
    initializeForJob,
  };

  return (
    <JobDrawerStateContext.Provider value={value}>
      {children}
    </JobDrawerStateContext.Provider>
  );
};

export const useJobDrawerState = () => {
  const context = React.useContext(JobDrawerStateContext);
  if (context === undefined) {
    throw new Error(
      'useJobDrawerState must be used within a JobDrawerStateProvider'
    );
  }
  return context;
};
